import { useState } from "react";
import { Camera, Search, X } from "lucide-react";

// 42 cinematic camera moves — labels short, hints richer for the underlying model
export const MOTION_PRESETS = [
  { id: "push-in",           label: "Push In",            hint: "slow cinematic push-in dolly toward the subject, gradually tightening frame" },
  { id: "pull-out",          label: "Pull Out",           hint: "smooth pull-out reveal dolly, subject stays centred as world expands" },
  { id: "pan-left",          label: "Pan Left",           hint: "steady horizontal pan to the left, gentle parallax across the plane" },
  { id: "pan-right",         label: "Pan Right",          hint: "steady horizontal pan to the right, gentle parallax across the plane" },
  { id: "tilt-up",           label: "Tilt Up",            hint: "vertical camera tilt upward, sky-reveal, cinematic anticipation" },
  { id: "tilt-down",         label: "Tilt Down",          hint: "vertical camera tilt downward, floor-reveal, cinematic weight" },
  { id: "zoom-in",           label: "Zoom In",            hint: "smooth optical zoom pulling detail in, static camera position" },
  { id: "zoom-out",          label: "Zoom Out",           hint: "smooth optical zoom pulling detail back, static camera position" },
  { id: "dolly-shot",        label: "Dolly Shot",         hint: "classic side dolly track alongside the subject, steady parallel motion" },
  { id: "tracking-shot",     label: "Tracking Shot",      hint: "camera tracks with the subject, matched pace, immersive follow" },
  { id: "crane-up",          label: "Crane Up",           hint: "cinematic crane rise, camera lifts from low to high, revealing scale" },
  { id: "crane-down",        label: "Crane Down",         hint: "cinematic crane descend, camera drops from high to low, focusing subject" },
  { id: "arc-left",          label: "Arc Left",           hint: "circular arc-left orbit, subject at pivot, cinematic reveal" },
  { id: "arc-right",         label: "Arc Right",          hint: "circular arc-right orbit, subject at pivot, cinematic reveal" },
  { id: "handheld-shake",    label: "Handheld Shake",     hint: "raw handheld camera micro-jitter, documentary energy" },
  { id: "steadicam-glide",   label: "Steadicam Glide",    hint: "steadicam smooth glide floating through the space" },
  { id: "whip-pan",          label: "Whip Pan",           hint: "fast whip pan transition, motion blur streaks across frame" },
  { id: "dutch-angle",       label: "Dutch Angle",        hint: "camera tilted off-axis, cinematic tension, off-kilter framing" },
  { id: "birds-eye",         label: "Bird's Eye",         hint: "top-down bird's-eye view, orthographic-feel drone shot" },
  { id: "worms-eye",         label: "Worm's Eye",         hint: "extreme low-angle worm's-eye upshot, monumental scale" },
  { id: "over-shoulder",     label: "Over-Shoulder",      hint: "over-the-shoulder view behind subject, cinematic dialogue framing" },
  { id: "pov",               label: "POV",                hint: "first-person point-of-view, camera as the character's eyes" },
  { id: "rack-focus",        label: "Rack Focus",         hint: "cinematic rack-focus pull from foreground to background subject" },
  { id: "spiral-in",         label: "Spiral In",          hint: "vertigo spiral inward, orbit + push-in combined, hypnotic" },
  { id: "spiral-out",        label: "Spiral Out",         hint: "vertigo spiral outward, orbit + pull-back, expansive reveal" },
  { id: "barrel-roll",       label: "Barrel Roll",        hint: "full 360° barrel roll rotation on the camera axis, dynamic" },
  { id: "orbit-left",        label: "Orbit Left",         hint: "smooth 180° orbit around subject to the left, revealing depth" },
  { id: "orbit-right",       label: "Orbit Right",        hint: "smooth 180° orbit around subject to the right, revealing depth" },
  { id: "dronie",            label: "Dronie",             hint: "drone selfie: camera pulls back and up simultaneously, epic reveal" },
  { id: "hyperlapse",        label: "Hyperlapse",         hint: "hyperlapse motion, fast-forward walking camera through space" },
  { id: "timelapse",         label: "Timelapse",          hint: "accelerated timelapse, clouds rushing, light shifting across scene" },
  { id: "parallax",          label: "Parallax",           hint: "layered parallax slide, foreground and background move at different speeds" },
  { id: "ken-burns",         label: "Ken Burns",          hint: "Ken Burns effect: slow pan and zoom on a still, documentary breath" },
  { id: "slide-left",        label: "Slide Left",         hint: "lateral slide-left camera, subject exits right edge" },
  { id: "slide-right",       label: "Slide Right",        hint: "lateral slide-right camera, subject exits left edge" },
  { id: "float-up",          label: "Float Up",           hint: "gentle vertical float upward, weightless, dream-like drift" },
  { id: "float-down",        label: "Float Down",         hint: "gentle vertical float downward, weightless, dream-like drift" },
  { id: "cinematic-drift",   label: "Cinematic Drift",    hint: "subtle organic drift, breathing camera, film-grade micro-motion" },
  { id: "snap-zoom",         label: "Snap Zoom",          hint: "fast punch-in snap zoom, aggressive attention grab" },
  { id: "slow-push",         label: "Slow Push",          hint: "very slow, patient push-in dolly, tension builds gradually" },
  { id: "fast-cut-zoom",     label: "Fast Cut Zoom",      hint: "rapid staccato cut-zoom pulses, edit-driven emphasis" },
  { id: "spin-360",          label: "360 Spin",           hint: "full 360° orbit around subject, camera circles once and returns" },
];

export function MotionPresetsPicker({ value, onSelect, testIdPrefix = "motion-preset" }) {
  const [q, setQ] = useState("");
  const filtered = MOTION_PRESETS.filter((p) =>
    !q.trim() || p.label.toLowerCase().includes(q.toLowerCase()) || p.id.includes(q.toLowerCase())
  );

  return (
    <div className="p-6 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15" data-testid="motion-presets-card">
      <div className="flex items-center justify-between mb-3">
        <label className="font-mono-hy text-[10px] text-[#F2C94C] flex items-center gap-1.5">
          <Camera className="w-3 h-3" /> Motion Presets · {MOTION_PRESETS.length}
        </label>
        {value && (
          <button
            onClick={() => onSelect(null)}
            data-testid="motion-preset-clear-btn"
            className="font-mono-hy text-[9px] text-[#E27D60] hover:text-white flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search 42 camera moves…"
          data-testid="motion-preset-search"
          className="w-full pl-8 pr-2 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:outline-none font-body text-xs text-white placeholder:text-white"
        />
      </div>
      <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1" data-testid="motion-preset-grid">
        {filtered.length === 0 && (
          <div className="col-span-2 py-4 text-center font-mono-hy text-[9px] text-white">No moves match "{q}".</div>
        )}
        {filtered.map((p) => {
          const active = value === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(active ? null : p.id)}
              data-testid={`${testIdPrefix}-${p.id}`}
              title={p.hint}
              className={`text-left font-mono-hy text-[10px] px-2.5 py-1.5 rounded-lg border transition-all truncate ${
                active
                  ? "bg-[#F2C94C] text-[#0A0A0A] border-[#F2C94C]"
                  : "bg-[#0A0A0A] border-[#F2C94C]/10 text-white hover:border-[#F2C94C]/40 hover:text-white"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {value && (
        <div className="mt-3 p-2 rounded-lg bg-[#00E5FF]/5 border border-[#00E5FF]/20" data-testid="motion-preset-active-hint">
          <div className="font-mono-hy text-[9px] text-[#00E5FF] mb-0.5">// ACTIVE</div>
          <div className="font-body text-[11px] text-[#D9D2BE] leading-relaxed">
            {MOTION_PRESETS.find((p) => p.id === value)?.hint}
          </div>
        </div>
      )}
    </div>
  );
}

export function resolveMotionPreset(presetId) {
  if (!presetId) return "";
  return MOTION_PRESETS.find((p) => p.id === presetId)?.hint || "";
}
